import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getRbacUsersLoadErrorMessage,
  parseRbacUsersPagination,
  parseRbacUsersPayload
} from '../components/helpers/rbacUserSelectionDialogHelpers'
import { fetchRbacUsers } from '../services/rbacApi'

export default function useRbacInfiniteUsers({
  open,
  pageSize,
  extraParams = {},
  scrollLoadThresholdPx = 80
}) {
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [loadError, setLoadError] = useState('')
  const requestIdRef = useRef(0)
  const pageRef = useRef(1)
  const hasMoreRef = useRef(false)
  const loadingMoreRef = useRef(false)
  const searchRef = useRef('')
  const extraParamsRef = useRef(extraParams)
  extraParamsRef.current = extraParams
  const extraParamsKey = JSON.stringify(extraParams || {})

  const loadUsers = useCallback(
    async ({ nextPage, search, replace }) => {
      if (!replace && loadingMoreRef.current) return

      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      if (replace) {
        setLoadingUsers(true)
        setLoadingMore(false)
        loadingMoreRef.current = false
      } else {
        loadingMoreRef.current = true
        setLoadingMore(true)
      }
      setLoadError('')

      try {
        const payload = await fetchRbacUsers({
          page: nextPage,
          per_page: pageSize,
          search: search || undefined,
          ...(extraParamsRef.current || {}),
          status: 'active',
          active: true
        })
        if (requestId !== requestIdRef.current) return

        const nextUsers = parseRbacUsersPayload(payload)
        const pagination = parseRbacUsersPagination(payload)
        const total = Number(pagination.total) || 0
        const currentPage = Number(pagination.page) || nextPage
        const perPage = Number(pagination.per_page) || pageSize
        const nextHasMore = currentPage * perPage < total

        setUsers((prev) => {
          if (replace) return nextUsers
          const seen = new Set(prev.map((user) => String(user.id)))
          return [...prev, ...nextUsers.filter((user) => !seen.has(String(user.id)))]
        })
        pageRef.current = currentPage
        hasMoreRef.current = nextHasMore
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        if (replace) {
          setUsers([])
          hasMoreRef.current = false
        }
        setLoadError(getRbacUsersLoadErrorMessage(err))
      } finally {
        if (requestId !== requestIdRef.current) return
        setLoadingUsers(false)
        setLoadingMore(false)
        loadingMoreRef.current = false
      }
    },
    [pageSize]
  )

  useEffect(() => {
    if (open) return

    requestIdRef.current += 1
    setLoadingUsers(true)
    setLoadingMore(false)
    loadingMoreRef.current = false
    setUsers([])
    pageRef.current = 1
    hasMoreRef.current = false
    setSearchInput('')
    searchRef.current = ''
    setLoadError('')
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    const query = searchInput.trim()
    searchRef.current = query
    const delayMs = query ? 300 : 0
    const timer = window.setTimeout(() => {
      pageRef.current = 1
      loadUsers({ nextPage: 1, search: query, replace: true })
    }, delayMs)

    return () => window.clearTimeout(timer)
  }, [extraParamsKey, loadUsers, open, searchInput])

  const handleListScroll = (event) => {
    if (!hasMoreRef.current || loadingMoreRef.current || loadingUsers) return
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight > scrollLoadThresholdPx) return
    loadUsers({
      nextPage: pageRef.current + 1,
      search: searchRef.current,
      replace: false
    })
  }

  return {
    users,
    loadingUsers,
    loadingMore,
    searchInput,
    setSearchInput,
    loadError,
    setLoadError,
    handleListScroll
  }
}
